// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Papershot — wiring only.
 *
 * The pipeline lives in `cv/` and runs in `worker.ts`; this file decodes what
 * the user gives us, moves pixels to and from the worker, and keeps the panels
 * in sync. It deliberately owns no image processing of its own.
 */

import { Camera, isCameraSupported } from './camera';
import { CornerEditor } from './corners';
import { EventLog } from './eventlog';
import { exportName, formatBytes, formatDimensions, pluralise } from './format';
import { initGlossary } from './glossary';
import { PageStore } from './pages';
import { buildPdf } from './pdf';
import * as ui from './ui';
import type {
  EnhanceMode,
  PaperSize,
  Preferences,
  Quad,
  ScanPage,
  WorkerRequest,
  WorkerResponse,
} from './types';
import './styles/main.css';

/** Phone photos run to 12 MP; past this the extra pixels cost time and buy nothing. */
const MAX_SOURCE_DIMENSION = 2400;
const FINAL_MAX_DIMENSION = 2200;
const PREVIEW_MAX_DIMENSION = 460;
const PREVIEW_DEBOUNCE_MS = 180;
const PREFS_KEY = 'papershot.prefs.v1';

const MODE_NOTES: Record<EnhanceMode, string> = {
  color:
    'Keeps colour. Best for anything where a stamp, a signature in blue ink or a highlighter mark matters.',
  gray: 'Neutral grey. A good middle ground for handwriting and mixed pages.',
  bw: 'Adaptive threshold — crisp text, smallest file. Best for printed pages.',
};

// ────────────────────────────────────────────────────────────── state ──

const store = new PageStore();
const camera = new Camera();

let eventLog: EventLog;
let cornerEditor: CornerEditor;

interface Session {
  id: string;
  name: string;
  width: number;
  height: number;
}

let session: Session | null = null;
let queue: File[] = [];
let previewTimer: number | undefined;
let latestPreviewToken = 0;
let busy = false;

const prefs: Preferences = loadPreferences();

function loadPreferences(): Preferences {
  const fallback: Preferences = { mode: 'bw', deshadow: true, paperSize: 'auto', quality: 85 };
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      mode: parsed.mode ?? fallback.mode,
      deshadow: parsed.deshadow ?? fallback.deshadow,
      paperSize: parsed.paperSize ?? fallback.paperSize,
      quality: parsed.quality ?? fallback.quality,
    };
  } catch {
    // A corrupt or blocked localStorage is not worth failing over.
    return fallback;
  }
}

function savePreferences(): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Private mode can refuse writes; preferences simply don't persist.
  }
}

// ───────────────────────────────────────────────────────── worker RPC ──

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

interface Waiter<T> {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
}

interface RenderResult {
  // Pinned to ArrayBuffer (not ArrayBufferLike) so it satisfies ImageData's
  // constructor, which rejects a possibly-shared backing buffer.
  data: Uint8ClampedArray<ArrayBuffer>;
  width: number;
  height: number;
}

const loadWaiters = new Map<string, Waiter<{ quad: Quad; detected: boolean }>>();
const renderWaiters = new Map<
  number,
  Waiter<RenderResult> & { onProgress?: (phase: string, fraction: number) => void }
>();

function send(request: WorkerRequest, transfer: Transferable[] = []): void {
  worker.postMessage(request, transfer);
}

worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
  const message = event.data;

  switch (message.type) {
    case 'loaded': {
      loadWaiters.get(message.id)?.resolve({ quad: message.quad, detected: message.detected });
      loadWaiters.delete(message.id);
      break;
    }
    case 'rendered': {
      renderWaiters.get(message.token)?.resolve({
        data: new Uint8ClampedArray(message.data),
        width: message.width,
        height: message.height,
      });
      renderWaiters.delete(message.token);
      break;
    }
    case 'progress': {
      renderWaiters.get(message.token)?.onProgress?.(message.phase, message.fraction);
      break;
    }
    case 'error': {
      const error = new Error(message.message);
      if (message.token !== undefined) {
        renderWaiters.get(message.token)?.reject(error);
        renderWaiters.delete(message.token);
      } else {
        loadWaiters.get(message.id)?.reject(error);
        loadWaiters.delete(message.id);
      }
      break;
    }
  }
};

worker.onerror = () => {
  const error = new Error('The image processor stopped unexpectedly. Reload the page to retry.');
  for (const waiter of loadWaiters.values()) waiter.reject(error);
  for (const waiter of renderWaiters.values()) waiter.reject(error);
  loadWaiters.clear();
  renderWaiters.clear();
  fail(error.message);
};

function loadIntoWorker(id: string, image: ImageData): Promise<{ quad: Quad; detected: boolean }> {
  return new Promise((resolve, reject) => {
    loadWaiters.set(id, { resolve, reject });
    const buffer = image.data.buffer as ArrayBuffer;
    send({ type: 'load', id, data: buffer, width: image.width, height: image.height }, [buffer]);
  });
}

function renderInWorker(
  id: string,
  quad: Quad,
  maxDimension: number,
  onProgress?: (phase: string, fraction: number) => void,
): { token: number; result: Promise<RenderResult> } {
  const token = ++latestPreviewToken;
  const result = new Promise<RenderResult>((resolve, reject) => {
    renderWaiters.set(token, { resolve, reject, onProgress });
    send({
      type: 'render',
      id,
      quad,
      mode: prefs.mode,
      deshadow: prefs.deshadow,
      maxDimension,
      token,
    });
  });
  return { token, result };
}

// ──────────────────────────────────────────────────────────── intake ──

function newId(): string {
  return `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** Draws a decoded image into the visible source canvas, capped in size. */
function drawSource(image: ImageBitmap | HTMLCanvasElement): ImageData {
  const canvas = ui.must<HTMLCanvasElement>('source-canvas');
  const longest = Math.max(image.width, image.height);
  const scale = Math.min(1, MAX_SOURCE_DIMENSION / longest);

  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('This browser would not give us a canvas to work on.');

  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return context.getImageData(0, 0, canvas.width, canvas.height);
}

async function beginEdit(source: Blob | HTMLCanvasElement, name: string): Promise<void> {
  ui.clearError();
  busy = true;

  try {
    let pixels: ImageData;
    if (source instanceof Blob) {
      const bitmap = await createImageBitmap(source);
      try {
        pixels = drawSource(bitmap);
      } finally {
        bitmap.close();
      }
    } else {
      pixels = drawSource(source);
    }

    const id = newId();
    session = { id, name, width: pixels.width, height: pixels.height };

    ui.must('file-name').textContent = name;
    ui.must('file-meta').textContent = formatDimensions(pixels.width, pixels.height);
    eventLog.add('info', `Opened ${name} (${formatDimensions(pixels.width, pixels.height)})`);

    cornerEditor.setImageSize(pixels.width, pixels.height);

    const { quad, detected } = await loadIntoWorker(id, pixels);
    cornerEditor.setQuad(quad);

    eventLog.add(
      detected ? 'good' : 'warn',
      detected
        ? 'Page corners detected'
        : 'No page edge found — drag the corners onto the page yourself',
    );
    ui.must('stage-hint').classList.toggle('is-warning', !detected);

    ui.showPanel('editor');
    schedulePreview();
  } catch (error) {
    fail(error instanceof Error ? error.message : 'That image could not be opened.');
  } finally {
    busy = false;
  }
}

function ingest(files: File[]): void {
  if (!files.length) return;
  queue = queue.concat(files);
  eventLog.add('info', `Queued ${pluralise(files.length, 'photo')}`);
  void nextFromQueue();
}

/**
 * Loads the bundled demo photo and feeds it through the exact same path a real
 * dropped/picked file takes — a skewed shot of a page on a dark surface, so the
 * corner detection and perspective flatten have something real to work on.
 */
async function loadSample(): Promise<void> {
  if (busy) return;
  ui.clearError();
  try {
    eventLog.add('info', 'Loading sample photo');
    const response = await fetch('samples/demo.jpg');
    if (!response.ok) throw new Error(`sample fetch ${response.status}`);
    const blob = await response.blob();
    const file = new File([blob], 'sample-document.jpg', { type: blob.type || 'image/jpeg' });
    ingest([file]);
  } catch (error) {
    fail(
      error instanceof Error && error.message
        ? `The sample could not be loaded (${error.message}).`
        : 'The sample could not be loaded.',
    );
  }
}

async function nextFromQueue(): Promise<void> {
  const file = queue.shift();
  if (!file) {
    finishBatch();
    return;
  }
  await beginEdit(file, file.name);
}

function finishBatch(): void {
  session = null;
  if (store.isEmpty) {
    ui.showPanel('intake');
    return;
  }
  refreshPages();
  ui.showPanel('result');
}

// ─────────────────────────────────────────────────────────── preview ──

function schedulePreview(): void {
  window.clearTimeout(previewTimer);
  previewTimer = window.setTimeout(() => void renderPreview(), PREVIEW_DEBOUNCE_MS);
}

async function renderPreview(): Promise<void> {
  const current = session;
  if (!current) return;

  const spinner = ui.must('preview-spinner');
  spinner.hidden = false;

  const { token, result } = renderInWorker(
    current.id,
    cornerEditor.getQuad(),
    PREVIEW_MAX_DIMENSION,
  );

  try {
    const rendered = await result;
    // A newer preview (or a full render) has been requested since; drop this one.
    if (token !== latestPreviewToken || session?.id !== current.id) return;

    const canvas = ui.must<HTMLCanvasElement>('preview-canvas');
    canvas.width = rendered.width;
    canvas.height = rendered.height;
    canvas
      .getContext('2d')
      ?.putImageData(new ImageData(rendered.data, rendered.width, rendered.height), 0, 0);
  } catch {
    // A failed preview isn't worth interrupting the user over — the corners are
    // probably mid-drag and degenerate. The full render reports properly.
  } finally {
    if (token === latestPreviewToken) spinner.hidden = true;
  }
}

// ──────────────────────────────────────────────────────── add a page ──

function encodeCanvas(canvas: HTMLCanvasElement, mode: EnhanceMode): Promise<Blob> {
  // Bilevel output compresses far better — and without ringing artefacts around
  // letterforms — as PNG than as JPEG.
  const png = mode === 'bw';
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The page could not be encoded.'))),
      png ? 'image/png' : 'image/jpeg',
      png ? undefined : prefs.quality / 100,
    );
  });
}

async function addPage(): Promise<void> {
  const current = session;
  if (!current || busy) return;

  busy = true;
  ui.clearError();
  ui.showPanel('progress');
  ui.setProgress('Flattening perspective', 0);

  try {
    const { result } = renderInWorker(
      current.id,
      cornerEditor.getQuad(),
      FINAL_MAX_DIMENSION,
      (phase, fraction) => ui.setProgress(phase, fraction),
    );
    const rendered = await result;

    ui.setProgress('Encoding page', 0.92);

    const canvas = document.createElement('canvas');
    canvas.width = rendered.width;
    canvas.height = rendered.height;
    canvas
      .getContext('2d')
      ?.putImageData(new ImageData(rendered.data, rendered.width, rendered.height), 0, 0);

    const blob = await encodeCanvas(canvas, prefs.mode);
    const page: ScanPage = {
      id: current.id,
      blob,
      format: prefs.mode === 'bw' ? 'png' : 'jpeg',
      url: URL.createObjectURL(blob),
      width: rendered.width,
      height: rendered.height,
      mode: prefs.mode,
      sourceName: current.name,
    };
    store.add(page);

    eventLog.add(
      'good',
      `Page ${store.count} added — ${formatDimensions(page.width, page.height)}, ${formatBytes(blob.size)}`,
    );

    send({ type: 'release', id: current.id });
    session = null;
    await nextFromQueue();
  } catch (error) {
    fail(error instanceof Error ? error.message : 'That page could not be processed.');
  } finally {
    busy = false;
  }
}

function discardPage(): void {
  if (!session) return;
  send({ type: 'release', id: session.id });
  eventLog.add('warn', `Discarded ${session.name}`);
  session = null;
  void nextFromQueue();
}

// ───────────────────────────────────────────────────────────── output ──

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  // Revoking immediately can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

async function makePdf(): Promise<{ blob: Blob; filename: string }> {
  const blob = await buildPdf(store.all, prefs.paperSize);
  return { blob, filename: exportName(store.count, 'pdf') };
}

async function downloadPdf(): Promise<void> {
  try {
    const { blob, filename } = await makePdf();
    saveBlob(blob, filename);
    eventLog.add('good', `PDF built in this tab — ${formatBytes(blob.size)}, never uploaded`);
    ui.must('result-meta').textContent = `Last export: ${filename} · ${formatBytes(blob.size)}`;
  } catch (error) {
    fail(error instanceof Error ? error.message : 'The PDF could not be built.');
  }
}

async function saveAs(): Promise<void> {
  const picker = window.showSaveFilePicker;
  if (!picker) {
    fail('This browser has no save dialog. Use Download instead.');
    return;
  }

  try {
    const { blob, filename } = await makePdf();
    const handle = await picker.call(window, {
      suggestedName: filename,
      types: [{ description: 'PDF document', accept: { 'application/pdf': ['.pdf'] } }],
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    eventLog.add('good', `Saved ${filename} (${formatBytes(blob.size)})`);
  } catch (error) {
    // An abort is the user changing their mind, not a failure.
    if (error instanceof DOMException && error.name === 'AbortError') return;
    fail(error instanceof Error ? error.message : 'The file could not be saved.');
  }
}

async function sharePdf(): Promise<void> {
  try {
    const { blob, filename } = await makePdf();
    const file = new File([blob], filename, { type: 'application/pdf' });
    if (!navigator.canShare?.({ files: [file] })) {
      fail('This browser will not share a PDF. Use Download instead.');
      return;
    }
    await navigator.share({ files: [file], title: 'Scan' });
    eventLog.add('good', `Handed ${filename} to the system share sheet`);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    fail(error instanceof Error ? error.message : 'Sharing failed.');
  }
}

async function downloadImages(): Promise<void> {
  const pages = store.all;
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    saveBlob(page.blob, `scan-page-${i + 1}.${page.format === 'png' ? 'png' : 'jpg'}`);
    // Browsers throttle back-to-back downloads; a beat between them avoids drops.
    if (i < pages.length - 1) await new Promise((resolve) => setTimeout(resolve, 350));
  }
  eventLog.add('good', `Downloaded ${pluralise(pages.length, 'image')}`);
}

async function copyPage(id: string): Promise<void> {
  const page = store.get(id);
  if (!page) return;
  try {
    await navigator.clipboard.write([new ClipboardItem({ [page.blob.type]: page.blob })]);
    eventLog.add('good', 'Page copied to the clipboard');
  } catch {
    fail('This browser refused the clipboard write. Download the page instead.');
  }
}

function canCopyImages(): boolean {
  return typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function';
}

function refreshPages(): void {
  ui.renderPageStrip(
    store.all,
    {
      onRemove: (id) => {
        store.remove(id);
        eventLog.add('warn', 'Page removed');
        if (store.isEmpty) {
          ui.showPanel('intake');
          return;
        }
        refreshPages();
      },
      onMove: (id, delta) => {
        if (store.move(id, delta)) refreshPages();
      },
      onCopy: (id) => void copyPage(id),
    },
    canCopyImages(),
  );

  ui.must('result-meta').textContent =
    `${pluralise(store.count, 'page')} · ${formatBytes(store.totalBytes)} held in memory only`;
}

// ──────────────────────────────────────────────────────────── errors ──

function fail(message: string): void {
  ui.showError(message);
  eventLog.add('bad', message);
  // Put the user somewhere they can act, rather than on a stalled progress bar.
  ui.showPanel(session ? 'editor' : store.isEmpty ? 'intake' : 'result');
}

// ──────────────────────────────────────────────────────────── camera ──

async function openCamera(): Promise<void> {
  ui.clearError();
  ui.showPanel('camera');
  try {
    await camera.start(ui.must<HTMLVideoElement>('camera-video'));
    eventLog.add('good', 'Camera opened — the stream stays in this tab');

    const torch = ui.must<HTMLButtonElement>('torch');
    torch.hidden = !camera.hasTorch;
    torch.setAttribute('aria-pressed', 'false');
  } catch (error) {
    closeCamera();
    const denied = error instanceof DOMException && error.name === 'NotAllowedError';
    fail(
      denied
        ? 'Camera access was declined. You can still drop in a photo you have already taken.'
        : 'No camera is available. You can still drop in a photo you have already taken.',
    );
  }
}

function closeCamera(): void {
  camera.stop();
  if (!ui.must('camera-panel').hidden) {
    ui.showPanel(store.isEmpty ? 'intake' : 'result');
  }
}

async function capture(): Promise<void> {
  const canvas = camera.capture();
  if (!canvas) {
    fail('The camera has not produced a frame yet. Give it a moment and try again.');
    return;
  }
  eventLog.add('info', `Captured ${formatDimensions(canvas.width, canvas.height)} frame`);
  camera.stop();
  await beginEdit(canvas, `camera-${store.count + 1}.png`);
}

// ────────────────────────────────────────────────────────────── boot ──

function applyModeButtons(): void {
  const group = ui.must('mode-group');
  for (const button of group.querySelectorAll<HTMLButtonElement>('[data-mode]')) {
    const active = button.dataset.mode === prefs.mode;
    button.setAttribute('aria-checked', active ? 'true' : 'false');
  }
  ui.must('mode-note').innerHTML = MODE_NOTES[prefs.mode];
  ui.must('quality-group').hidden = prefs.mode === 'bw';
}

function init(): void {
  eventLog = new EventLog(ui.must('eventlog'), ui.must<HTMLButtonElement>('eventlog-toggle'));
  ui.initModals();
  initGlossary();

  cornerEditor = new CornerEditor(ui.must('corner-layer'), () => schedulePreview());

  ui.initDropzone(ingest);

  // The sample pill sits inside the dropzone, whose own click opens the file
  // picker — stop the event so tapping the pill loads the demo instead.
  ui.must<HTMLButtonElement>('sample-btn').addEventListener('click', (event) => {
    event.stopPropagation();
    void loadSample();
  });

  // ── intake / camera
  const cameraButton = ui.must<HTMLButtonElement>('open-camera');
  cameraButton.hidden = !isCameraSupported();
  cameraButton.addEventListener('click', () => void openCamera());
  ui.must('camera-close').addEventListener('click', () => closeCamera());
  ui.must('shutter').addEventListener('click', () => void capture());

  const torch = ui.must<HTMLButtonElement>('torch');
  torch.addEventListener('click', () => {
    const next = torch.getAttribute('aria-pressed') !== 'true';
    torch.setAttribute('aria-pressed', next ? 'true' : 'false');
    void camera.setTorch(next).catch(() => {
      torch.hidden = true;
    });
  });

  // ── editor controls
  ui.must('mode-group').addEventListener('click', (event) => {
    const button = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-mode]');
    if (!button?.dataset.mode) return;
    prefs.mode = button.dataset.mode as EnhanceMode;
    savePreferences();
    applyModeButtons();
    schedulePreview();
  });

  const deshadow = ui.must<HTMLInputElement>('deshadow');
  deshadow.checked = prefs.deshadow;
  deshadow.addEventListener('change', () => {
    prefs.deshadow = deshadow.checked;
    savePreferences();
    schedulePreview();
  });

  const quality = ui.must<HTMLInputElement>('quality');
  const qualityValue = ui.must('quality-value');
  quality.value = String(prefs.quality);
  qualityValue.textContent = `${prefs.quality}%`;
  quality.addEventListener('input', () => {
    prefs.quality = Number(quality.value);
    qualityValue.textContent = `${prefs.quality}%`;
    savePreferences();
  });

  ui.must('reset-corners').addEventListener('click', () => {
    if (!session) return;
    cornerEditor.setQuad([
      { x: session.width * 0.08, y: session.height * 0.08 },
      { x: session.width * 0.92, y: session.height * 0.08 },
      { x: session.width * 0.92, y: session.height * 0.92 },
      { x: session.width * 0.08, y: session.height * 0.92 },
    ]);
    schedulePreview();
  });

  ui.must('add-page').addEventListener('click', () => void addPage());
  ui.must('discard-page').addEventListener('click', () => discardPage());

  // ── result controls
  const paperSize = ui.must<HTMLSelectElement>('paper-size');
  paperSize.value = prefs.paperSize;
  paperSize.addEventListener('change', () => {
    prefs.paperSize = paperSize.value as PaperSize;
    savePreferences();
  });

  ui.must('download-pdf').addEventListener('click', () => void downloadPdf());
  ui.must('download-images').addEventListener('click', () => void downloadImages());
  ui.must('scan-another').addEventListener('click', () => ui.showPanel('intake'));

  const saveAsButton = ui.must<HTMLButtonElement>('save-as');
  saveAsButton.hidden = typeof window.showSaveFilePicker !== 'function';
  saveAsButton.addEventListener('click', () => void saveAs());

  const shareButton = ui.must<HTMLButtonElement>('share');
  shareButton.hidden = typeof navigator.canShare !== 'function';
  shareButton.addEventListener('click', () => void sharePdf());

  ui.must('error-retry').addEventListener('click', () => {
    ui.clearError();
    if (session) schedulePreview();
  });

  // ── keyboard
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (!ui.must('camera-panel').hidden) closeCamera();
      return;
    }
    if (event.key !== 'Enter' || ui.isModalOpen()) return;

    const target = event.target as HTMLElement | null;
    if (target && /^(BUTTON|INPUT|SELECT|TEXTAREA|A)$/.test(target.tagName)) return;

    if (!ui.must('editor').hidden) {
      event.preventDefault();
      void addPage();
    } else if (!ui.must('result-panel').hidden) {
      event.preventDefault();
      void downloadPdf();
    }
  });

  // Never leave the camera indicator lit after the user has navigated away.
  window.addEventListener('pagehide', () => camera.stop());
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) camera.stop();
  });

  applyModeButtons();
  ui.showPanel('intake');
  eventLog.add('good', 'Ready — everything below runs on this device');
}

init();
